import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { MentionRef } from "./types";

export interface AssistantActions {
  openAssistant: (prompt?: string, mentions?: MentionRef[]) => void;
  openAssistantOnboarding: () => void;
  closeAssistant: () => void;
}

interface AssistantRequest {
  prompt?: string;
  mentions: MentionRef[];
}

interface AssistantBridge {
  register: (
    opener: (request: AssistantRequest) => void,
    closer: () => void,
    onboarding: () => void
  ) => void;
}

export const AssistantActionsContext =
  createContext<AssistantActions | null>(null);

const AssistantBridgeContext = createContext<AssistantBridge | null>(null);

export function useAssistantActions(): AssistantActions | null {
  return useContext(AssistantActionsContext);
}

export function useAssistantBridge(): AssistantBridge | null {
  return useContext(AssistantBridgeContext);
}

/**
 * Lightweight context for page-level Ask AI actions. The launcher (which owns
 * the streaming assistant hook and the drawer) registers itself; requests made
 * before it mounts are queued and replayed on registration.
 */
export function AssistantProvider({ children }: { children: ReactNode }) {
  const openerRef = useRef<((request: AssistantRequest) => void) | null>(null);
  const closerRef = useRef<(() => void) | null>(null);
  const onboardingRef = useRef<(() => void) | null>(null);
  const pendingRef = useRef<AssistantRequest | null>(null);
  const pendingOnboardingRef = useRef(false);

  const openAssistant = useCallback(
    (prompt?: string, mentions: MentionRef[] = []) => {
      const request = { prompt, mentions };
      if (openerRef.current) {
        openerRef.current(request);
        return;
      }
      pendingRef.current = request;
    },
    []
  );

  const closeAssistant = useCallback(() => {
    closerRef.current?.();
  }, []);

  const openAssistantOnboarding = useCallback(() => {
    if (onboardingRef.current) {
      onboardingRef.current();
      return;
    }
    pendingOnboardingRef.current = true;
  }, []);

  const register = useCallback(
    (
      opener: (request: AssistantRequest) => void,
      closer: () => void,
      onboarding: () => void
    ) => {
      openerRef.current = opener;
      closerRef.current = closer;
      onboardingRef.current = onboarding;

      if (pendingOnboardingRef.current) {
        pendingOnboardingRef.current = false;
        onboarding();
      }

      const pending = pendingRef.current;
      if (pending) {
        pendingRef.current = null;
        opener(pending);
      }
    },
    []
  );

  const actions = useMemo(
    () => ({ openAssistant, openAssistantOnboarding, closeAssistant }),
    [openAssistant, openAssistantOnboarding, closeAssistant]
  );
  const bridge = useMemo(() => ({ register }), [register]);

  return (
    <AssistantActionsContext.Provider value={actions}>
      <AssistantBridgeContext.Provider value={bridge}>
        {children}
      </AssistantBridgeContext.Provider>
    </AssistantActionsContext.Provider>
  );
}

export function AskAiButton({
  prompt,
  mentions,
}: {
  prompt?: string;
  mentions?: MentionRef[];
}) {
  const actions = useAssistantActions();
  if (!actions) return null;

  return (
    <Button
      variant="outline"
      className="hidden lg:inline-flex"
      onClick={() => actions.openAssistant(prompt, mentions)}
    >
      <Sparkles className="mr-2 h-4 w-4" />
      Ask AI
    </Button>
  );
}
